import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
  getFilteredRowModel,
  ColumnFiltersState,
  getSortedRowModel,
} from '@tanstack/react-table';

import { Table, TableBody, TableHead, TableHeader, TableRow, TableCell } from '@/components/ui/table';
import { Typography } from '@/components';
import { cn } from '@/utils/styles';
import { useEffect, useState } from 'react';
import { useCompanyRole } from '@/features/company/hooks';
import { getWidthClass } from '@/helpers/table-columns-width';
import { OpportunityItemList } from '@/features/opportunities/types';
import { OpportunitiesFilter } from '../filters';
import { OpportunitiesEmptyState } from '../empty-state';
import { DataTablePagination } from './opportunities-pagination';
import { OpportunityRow } from './opportunity-row';
import { useSearchParams } from 'react-router-dom';
import { useCountries, useDevelopmentStatus, useSaleOptions } from '@/hooks';
import { customGlobalFilterFn } from '@/features/opportunities/helpers/custom-global-filter-fn';
import { resolveTechAliases } from '@/features/opportunities/helpers/search-alias';

interface DataTableProps<OpportunityItemList, TValue> {
  onResetFilters: () => void;
  columns: ColumnDef<OpportunityItemList, TValue>[];
  data: OpportunityItemList[];
  datasetMaxCapacity?: number;
}

export function OpportunitiesTable<TData, TValue>({
  onResetFilters,
  columns,
  data,
  datasetMaxCapacity,
}: DataTableProps<OpportunityItemList, TValue>) {
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const searchValue = searchParams.get('search') ?? '';
  const [globalFilter, setGlobalFilter] = useState<string>(searchValue);

  const { options: countries } = useCountries();
  const { map: mapDevelopmentStatus } = useDevelopmentStatus();
  const { map: mapSaleOption } = useSaleOptions();

  useEffect(() => {
    const searchValue = searchParams.get('search') ?? '';
    setGlobalFilter(searchValue);

    const newColumnFilters: ColumnFiltersState = [];

    const technology = searchParams.get('technology');
    if (technology) {
      newColumnFilters.push({ id: 'technologies', value: technology.split(',') });
    }

    const developmentStatus = searchParams.get('development_status');
    if (developmentStatus) {
      newColumnFilters.push({ id: 'status', value: developmentStatus.split(',') });
    }

    const dealType = searchParams.get('deal_type');
    if (dealType) {
      newColumnFilters.push({ id: 'options', value: dealType.split(',') });
    }

    const country = searchParams.get('country');
    if (country) {
      newColumnFilters.push({ id: 'country', value: country.split(',') });
    }

    const capacity = searchParams.get('capacity');
    if (capacity) {
      newColumnFilters.push({ id: 'capacity', value: capacity.split(',') });
    }

    const typeOfRevenue = searchParams.get('type_of_revenue');
    if (typeOfRevenue) {
      newColumnFilters.push({ id: 'typeOfRevenue', value: typeOfRevenue.split(',') });
    }

    setColumnFilters(newColumnFilters);
  }, [searchParams]);

  const table = useReactTable({
    data,
    columns,
    state: { globalFilter, columnFilters },
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    globalFilterFn: (row, columnId, filterValue) => {
      const trimmed = filterValue.trim();
      if (!trimmed) return true;
      const techMatches = resolveTechAliases(trimmed);
      if (techMatches.length > 0) {
        const techsInRow = row.getValue('technologies') as string[];
        return techMatches.some((match) => techsInRow.includes(match.value));
      }
      return customGlobalFilterFn(row, columnId, filterValue, countries, mapDevelopmentStatus, mapSaleOption);
    },
    initialState: { pagination: { pageSize: 50 }, columnVisibility: { typeOfRevenue: false } },
  });

  const { isPremium } = useCompanyRole();
  return (
    <>
      <div className="flex justify-between py-3 md:py-0">
        <OpportunitiesFilter
          onResetFilters={onResetFilters}
          table={table}
          datasetMaxCapacity={datasetMaxCapacity}
          search={globalFilter}
          onSearchChange={setGlobalFilter}
          searchParams={searchParams}
          onParamsChange={setSearchParams}
        />
      </div>

      {table.getRowModel().rows?.length ? (
        <>
          <div
            className={cn(
              !isPremium && 'lg:mt-8',
              'mt-4 w-0 min-w-full',
              'bg-neutral-10 overflow-x-auto rounded-md border-2 border-neutral-50'
            )}
          >
            <Table className="w-full min-w-[1100px] table-fixed lg:min-w-0">
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id} className="hover:bg-muted border-neutral-100">
                    {headerGroup.headers.map((header) => {
                      const widthClass = getWidthClass('opportunities', header.column.id);
                      return (
                        <TableHead key={header.id} className={`px-3 py-4 ${widthClass}`}>
                          <Typography
                            as="h6"
                            size="body-2"
                            weight="semibold"
                            className="whitespace-nowrap text-neutral-500"
                          >
                            {header.isPlaceholder
                              ? null
                              : flexRender(header.column.columnDef.header, header.getContext())}
                          </Typography>
                        </TableHead>
                      );
                    })}
                  </TableRow>
                ))}
              </TableHeader>

              <TableBody className="border-b-0 border-neutral-100 bg-white">
                {table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map((row) => {
                    return <OpportunityRow key={row.id} row={row} isPremium={isPremium} />;
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={columns.length} className="h-24 text-center">
                      No results.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          <DataTablePagination table={table} />
        </>
      ) : (
        <OpportunitiesEmptyState onResetFilters={onResetFilters} />
      )}
    </>
  );
}
